import { useKeycloak } from 'all-in-one-react-keycloak';
import { useAdminSearchUser, AdminSearchUserData, useAdminSelectUser } from 'all-in-one-shared-api';
import { Empty, Input, Space, Table, Typography } from 'antd';
import { useCachedClaimProperties, useKeycloakReady } from 'hooks';
import jwtDecode from 'jwt-decode';
import { useEffect, useState, useMemo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import NoResultIcon from 'assets/images/search-user-no-result.svg';
import { HomeContext } from 'contexts/home/context';
import { useProfileInfoContext } from 'contexts/profileInfo/context';
import { useInboxMessagesContext } from 'contexts/inboxMessages/context';
import { Spinner } from 'components';
import { useClaimPropertiesContext } from 'contexts/claimProperties';
import { StyledTitle } from 'components/StyledTitle';
import styled from 'styled-components';
import { osName, osVersion } from 'react-device-detect';
import { KeycloakClient, KeycloakTokenParsed } from '@react-keycloak/keycloak-ts';

const { Title, Text } = Typography;

const StyledInputSearch = styled(Input.Search)`
  margin-top: 2.5rem;
`;

const StyledEmpty = styled(Empty)`
  margin-top: 2.5rem;
`;
type Newkey = KeycloakClient & {
  tokenParsed?: KeycloakTokenParsed & {
    ilClientKey?: string;
    ebClientKey?: string;
  };
};

const ViewAsUserPage = () => {
  const [selectedUser, setSelectedUser] = useState<AdminSearchUserData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>();

  const { keycloak } = useKeycloak();
  const newKeycloak = keycloak as Newkey;
  const { token, ready } = useKeycloakReady();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mutate: homeMutate } = useContext(HomeContext);
  const { mutate: profileMutate } = useProfileInfoContext();
  const { key: claimPropertiesKey } = useClaimPropertiesContext();
  const { mutate: claimPropertiesMutate } = useCachedClaimProperties(claimPropertiesKey);
  const { fetchInboxMessages } = useInboxMessagesContext();
  const [loading, setLoading] = useState<boolean>(false);

  const { shouldFetch, data: userData } = useAdminSearchUser(
    { key: 'adminSearchUser', searchTerm },
    token ?? '',
    0,
    10,
    searchTerm,
    {
      onSuccess: () => {
        setLoading(false);
      },
      onError: () => {
        setLoading(false);
      },
    },
  );

  const { shouldFetch: shouldSelectUser } = useAdminSelectUser(
    {
      key: 'adminSelectUser',
      params: {
        memberId: selectedUser?.memberId,
        memberType: selectedUser?.memberType,
        policyNumber: selectedUser?.policyNumber,
        viewAsLoginID: selectedUser?.username,
      },
    },
    token ?? '',
    selectedUser?.memberId ?? '',
    selectedUser?.memberType ?? '',
    selectedUser?.policyNumber ?? '',
    selectedUser?.username ?? '',
    {
      onSuccess: (data) => {
        shouldSelectUser(false);
        if (!data.success) {
          return;
        }
        setIsLoading(true);
        const { preferred_username }: { preferred_username: string } = jwtDecode(keycloak.token ?? '');

        keycloak
          .login({
            action: 'refresh_token',
            username: preferred_username,
            refreshToken: keycloak.refreshToken,
            device: osName,
            deviceVersion: osVersion,
          })
          .then(async () => {
            // keycloak.updateToken(-1);
            console.log('tokenParsed==================================', keycloak.token);
            if (newKeycloak.tokenParsed?.ebClientKey == '' && newKeycloak.tokenParsed?.ilClientKey != '') {
              return navigate('/LIHome');
            }
            homeMutate && (await homeMutate());
            profileMutate && (await profileMutate());
            claimPropertiesMutate && (await claimPropertiesMutate());
            await fetchInboxMessages();
            navigate('/home');
          })
          .finally(() => {
            setIsLoading(false);
          });
      },
    },
  );

  useEffect(() => {
    shouldFetch(ready);
  }, [ready, shouldFetch]);

  useEffect(() => {
    shouldSelectUser(selectedUser !== null);
  }, [
    selectedUser,
    selectedUser?.memberId,
    selectedUser?.memberType,
    selectedUser?.policyNumber,
    selectedUser?.username,
    shouldSelectUser,
  ]);

  const dependent = useMemo(() => {
    if (!userData || !userData?.length) return [];

    if (userData.length === 1 && userData[0]?.families) {
      return userData[0]?.families.map((family) => ({
        id: family.id,
        policyNumber: family.policyNumber,
        memberId: family.memberId,
        memberType: family.memberType,
        givenName: family.firstName,
        surname: family.lastName,
        username: family.username,
        email: family.email,
      }));
    }
  }, [userData]);

  const columns = [
    {
      title: t('profile.name'),
      key: 'name',
      render: (value: AdminSearchUserData) => {
        return `${value.givenName} ${value.surname}`;
      },
    },
    {
      title: t('profile.email'),
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: t('profile.secondaryEmail'),
      dataIndex: 'secondaryEmail',
      key: 'secondaryEmail',
    },
    {
      title: t('profile.mobile'),
      dataIndex: 'mobile',
      key: 'mobile',
    },
    {
      title: t('profile.selfDefinedId'),
      dataIndex: 'username',
      key: 'username',
    },
    {
      dataIndex: '',
      key: 'x',
      render: (value: AdminSearchUserData) => {
        return <a onClick={() => setSelectedUser(value)}>{t('admin.viewAsUser.viewAs')}</a>;
      },
    },
  ];

  if (isLoading) return <Spinner />;

  return (
    <>
      <Title level={2}>{t('admin.viewAsUser.title')}</Title>
      <StyledInputSearch
        placeholder={t('admin.viewAsUser.searchPlaceholder')}
        onSearch={(value: string) => {
          setLoading(true);
          setSearchTerm(value);
        }}
      />

      {loading ? (
        <StyledTitle margin="2rem 0 0 0" level={4}>
          <Spinner />
        </StyledTitle>
      ) : (userData ?? []).length > 0 && Boolean(searchTerm) ? (
        <>
          <StyledTitle margin="2rem 0 0 0" level={4}>
            {t('admin.viewAsUser.principalResult')}
          </StyledTitle>
          <Table
            dataSource={userData}
            columns={columns}
            rowKey={(record) => `${record.email}-${record.memberId}`}
            pagination={false}
          />
          {dependent && dependent.length && (
            <>
              <StyledTitle margin="2rem 0 0 0" level={4}>
                {t('admin.viewAsUser.dependentResult')}
              </StyledTitle>
              <Table
                dataSource={dependent}
                columns={columns.filter((item) => item.key != 'mobile')}
                rowKey={(record) => record.id}
                pagination={false}
              />
            </>
          )}
        </>
      ) : (
        <StyledEmpty
          image={NoResultIcon}
          description={
            <Space direction="vertical" size="small">
              <Title level={3}>{t('admin.viewAsUser.noResult')}</Title>
              <Text type="secondary">{t('admin.viewAsUser.searchAgain')}</Text>
            </Space>
          }
        />
      )}
    </>
  );
};

export default ViewAsUserPage;
